import { Check, Zap, Diamond, Info, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const PricingSection = () => {
  // Preços fixos - cobrado anualmente
  const lightPrice = "397";
  const elitePrice = "497";

  // Implementação
  const lightImplOriginal = 1497;
  const lightImplPrice = 997;
  const lightImplDiscount = Math.round(((lightImplOriginal - lightImplPrice) / lightImplOriginal) * 100);

  const eliteImplOriginal = 3497;
  const eliteImplPrice = 1497;
  const eliteImplDiscount = Math.round(((eliteImplOriginal - eliteImplPrice) / eliteImplOriginal) * 100);

  return (
    <section id="planos" className="py-16 md:py-24 relative" aria-labelledby="pricing-heading">
      {/* Background Effects */}
      <div className="absolute top-1/2 left-1/4 w-60 md:w-80 h-60 md:h-80 bg-cyber-cyan/10 rounded-full blur-[80px] md:blur-[100px]" />
      <div className="absolute top-1/2 right-1/4 w-72 md:w-96 h-72 md:h-96 bg-electric-purple/15 rounded-full blur-[80px] md:blur-[100px]" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-12"
        >
          <h2 id="pricing-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-3 md:mb-4">
            Agilidade ou Segurança?{" "}
            <span className="text-gradient-zavvi">Você Decide.</span>
          </h2>
          <p className="text-base md:text-lg text-body-text px-4">
            Planos cobrados anualmente. Sem cobrança por atendente.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto items-start">
          {/* Light Plan */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.02 }}
            className="glass-card p-6 md:p-8 rounded-2xl md:rounded-3xl neon-border-cyan"
          >
            <div className="text-center mb-6 md:mb-8">
              <p className="text-xs md:text-sm text-cyber-cyan font-medium mb-2">O ACELERADOR</p>
              <div className="flex items-center justify-center gap-2 mb-3 md:mb-4">
                <Zap size={24} className="md:w-7 md:h-7 text-cyber-cyan fill-cyber-cyan" />
                <h3 className="text-2xl md:text-3xl font-display font-bold">
                  Zavvi <span className="text-cyber-cyan">Light</span>
                </h3>
              </div>
              
              <p className="text-body-text text-xs md:text-sm mb-4 px-2">
                <strong>Ideal para:</strong> Equipes que precisam começar rápido.
              </p>

              <div className="flex justify-center gap-2 md:gap-3 text-[10px] md:text-xs text-muted-foreground mb-4 md:mb-6 flex-wrap">
                <span className="glass-card px-2 md:px-3 py-1 rounded-full">⚡ Conexão Imediata</span>
                <span className="glass-card px-2 md:px-3 py-1 rounded-full">Scan & Go</span>
              </div>

              <div className="mb-2">
                <span className="text-muted-foreground text-lg md:text-xl">R$</span>
                <span className="text-4xl md:text-6xl font-display font-bold text-foreground mx-1">
                  {lightPrice}
                </span>
                <span className="text-muted-foreground text-base md:text-lg">/mês</span>
              </div>
              <p className="text-xs md:text-sm text-cyber-cyan">cobrado anualmente</p>
            </div>

            <div className="mb-6 md:mb-8">
              <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">O que está incluso:</p>
              <ul className="space-y-2 md:space-y-3">
                {[
                  "Custo zero por mensagem",
                  "Conexão imediata via QR Code",
                  "Atendentes ilimitados",
                  "Múltiplas conexões WhatsApp",
                  "Dashboard com métricas",
                  "Suporte brasileiro",
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 md:gap-3">
                    <div className="w-4 md:w-5 h-4 md:h-5 rounded-full flex items-center justify-center bg-cyber-cyan/20 text-cyber-cyan mt-0.5 flex-shrink-0">
                      <Check size={10} className="md:w-3 md:h-3" />
                    </div>
                    <span className="text-body-text text-xs md:text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Implementação Light */}
            <div className="mb-6 md:mb-8 p-4 rounded-xl bg-muted/50 border border-cyber-cyan/20">
              <div className="flex items-center gap-2 mb-2">
                <Tag size={14} className="text-cyber-cyan" />
                <span className="text-xs md:text-sm font-medium">Implementação (opcional)</span>
                <span className="bg-success-green/20 text-success-green text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full">
                  -{lightImplDiscount}%
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-muted-foreground line-through text-sm">R$ {lightImplOriginal.toLocaleString('pt-BR')}</span>
                <span className="text-cyber-cyan font-bold text-lg">R$ {lightImplPrice.toLocaleString('pt-BR')}</span>
              </div>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-1">
                Implementação completa pela equipe Zavvi
              </p>
            </div>

            <Button 
              variant="light" 
              size="lg" 
              className="w-full text-sm md:text-base"
              asChild
            >
              <a href="https://mpago.li/17oBgCn" target="_blank" rel="noopener noreferrer">
                Começar agora
              </a>
            </Button>
          </motion.div>

          {/* Elite Plan */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.02 }}
            className="relative glass-card p-6 md:p-8 rounded-2xl md:rounded-3xl neon-border-purple md:scale-[1.02] md:-mt-4 md:mb-4"
          >
            <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2">
              <div className="bg-gradient-elite px-4 md:px-5 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-bold text-foreground glow-purple whitespace-nowrap">
                ⭐ MAIS ESCOLHIDO
              </div>
            </div>

            <div className="text-center mb-6 md:mb-8 pt-2">
              <p className="text-xs md:text-sm text-electric-purple font-medium mb-2">A FORTALEZA</p>
              <div className="flex items-center justify-center gap-2 mb-3 md:mb-4">
                <Diamond size={24} className="md:w-7 md:h-7 text-electric-purple fill-electric-purple" />
                <h3 className="text-2xl md:text-3xl font-display font-bold">
                  Zavvi <span className="text-electric-purple">Elite</span>
                </h3>
              </div>
              
              <p className="text-body-text text-xs md:text-sm mb-4 px-2">
                <strong>Ideal para:</strong> Operações que não podem parar.
              </p>

              <div className="flex justify-center gap-2 md:gap-3 text-[10px] md:text-xs text-muted-foreground mb-4 md:mb-6 flex-wrap">
                <span className="glass-card px-2 md:px-3 py-1 rounded-full">💎 API Oficial Meta</span>
                <span className="glass-card px-2 md:px-3 py-1 rounded-full">Zero Ban</span>
              </div>

              <div className="mb-2">
                <span className="text-muted-foreground text-lg md:text-xl">R$</span>
                <span className="text-4xl md:text-6xl font-display font-bold text-foreground mx-1">
                  {elitePrice}
                </span>
                <span className="text-muted-foreground text-base md:text-lg">/mês</span>
              </div>
              <p className="text-xs md:text-sm text-electric-purple">cobrado anualmente</p>
            </div>

            <div className="mb-4 md:mb-6">
              <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">Tudo do Light, mais:</p>
              <ul className="space-y-2 md:space-y-3">
                {[
                  "Risco ZERO de banimento",
                  "Estabilidade 99.9% SLA",
                  "Webhooks e automações",
                  "Relatórios avançados",
                  "Suporte prioritário",
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 md:gap-3">
                    <div className="w-4 md:w-5 h-4 md:h-5 rounded-full flex items-center justify-center bg-electric-purple/20 text-electric-purple mt-0.5 flex-shrink-0">
                      <Diamond size={8} className="md:w-[10px] md:h-[10px]" />
                    </div>
                    <span className="text-body-text text-xs md:text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-start gap-2 p-2 md:p-3 rounded-lg bg-muted/50 mb-4 md:mb-6">
              <Info size={14} className="md:w-4 md:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-[10px] md:text-xs text-muted-foreground">
                Custo por mensagem conforme tabela Meta. <strong>Sem taxas escondidas.</strong>
              </p>
            </div>

            {/* Implementação Elite */}
            <div className="mb-6 md:mb-8 p-4 rounded-xl bg-electric-purple/10 border border-electric-purple/20">
              <div className="flex items-center gap-2 mb-2">
                <Tag size={14} className="text-electric-purple" />
                <span className="text-xs md:text-sm font-medium">Implementação (opcional)</span>
                <span className="bg-success-green/20 text-success-green text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full">
                  -{eliteImplDiscount}%
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-muted-foreground line-through text-sm">R$ {eliteImplOriginal.toLocaleString('pt-BR')}</span>
                <span className="text-electric-purple font-bold text-lg">R$ {eliteImplPrice.toLocaleString('pt-BR')}</span>
              </div>
              <p className="text-[10px] md:text-xs text-muted-foreground mt-1">
                Implementação completa + configuração avançada pela equipe Zavvi
              </p>
            </div>

            <Button 
              variant="elite" 
              size="lg" 
              className="w-full text-sm md:text-base"
              onClick={() => window.open('https://mpago.li/2ZzaTwW', '_blank')}
            >
              Começar agora
            </Button>
          </motion.div>
        </div>

        {/* Bottom Note - Competition Comparison */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-10 md:mt-16 max-w-3xl mx-auto text-center"
        >
          <div className="glass-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-electric-purple/20">
            <h4 className="font-display font-bold text-base md:text-lg mb-2 md:mb-3 text-gradient-zavvi">
              Por que a Zavvi?
            </h4>
            <p className="text-body-text text-xs md:text-sm leading-relaxed">
              A concorrência <span className="text-destructive font-semibold">não oferece cadastro ilimitado de agentes</span> nem 
              <span className="text-destructive font-semibold"> conexão ilimitada de números</span>. 
              Na Zavvi você tem <span className="text-cyber-cyan font-semibold">assentos ilimitados</span>, 
              <span className="text-electric-purple font-semibold"> segurança total com API Oficial</span> e
              <span className="text-cyber-cyan font-semibold"> agilidade máxima via QR Code</span>.
            </p>
          </div>
        </motion.div>

        <p className="text-center text-muted-foreground mt-6 md:mt-8 text-xs md:text-sm">
          ✓ Suporte brasileiro &nbsp;•&nbsp; ✓ Atendentes ilimitados
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
